import { errorCodes, getUserFriendlyErrorMessage } from '@/utils/errormessages'
import { apiErrorCodes } from '@/utils/apiHelper'
import { VISIT_TYPE } from '@/utils/constants'
import { isRecord } from '@/utils/narrowing'

/**
 * Typed reproduction of the error-mapping the `newClinicMember` reducer ran for the
 * create-member / validate-phone flows. The Orval `fetcher` throws an `AppError`
 * (`{ status, code, message, details }`) where `details` is the parsed server body —
 * the same object the old `apiCall` placed on `action.payload`. We narrow that body and
 * map it to the exact same UI state the page previously read from Redux.
 */

export interface DuplicateLicensesInfo {
  licenseNumber: string
  stateCode: string
  licensedBy: string
}

export interface MemberErrorResult {
  /** Friendly message shown via ErrorAlert / field errors. */
  error: string
  /** Set false when the failure was a phone-number validation/uniqueness problem. */
  phoneIsValid: boolean | null
  /** Server-reported duplicate license details, if any. */
  duplicateLicensesInfo: DuplicateLicensesInfo | null
}

const getString = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * Pulls the parsed server body out of whatever the mutation rejected with.
 * AppError carries it on `details`; we also accept a bare body object as a fallback.
 */
export const getServerErrorBody = (error: unknown): Record<string, unknown> => {
  if (isRecord(error) && 'details' in error && isRecord(error.details)) {
    return error.details
  }
  if (isRecord(error)) {
    return error
  }
  return {}
}

/**
 * Mirrors the reducer's error branch (CREATE_NEW_MEMBER_*_ERROR / EDIT_*_ERROR /
 * SET_*_PROFILE_IMG_ERROR / VALIDATE_PHONE_NUMBER_ERROR). Returns the friendly message
 * plus the phone/duplicate-license side state the page consumes.
 */
export const mapMemberError = (error: unknown): MemberErrorResult => {
  const body = getServerErrorBody(error)

  const hasUserFriendlyCode =
    'UserFriendlyErrorCode' in body && (Boolean(body.UserFriendlyErrorCode) || body.UserFriendlyErrorCode === '')

  if (hasUserFriendlyCode) {
    const code = getString(body.UserFriendlyErrorCode)
    const extra = typeof body.extra === 'string' ? body.extra : undefined
    return {
      error: code ? getUserFriendlyErrorMessage(code, null, extra) : '',
      phoneIsValid: null,
      duplicateLicensesInfo: null,
    }
  }

  const errorCode = getString(body.ErrorCode)
  const data = isRecord(body.Data) ? body.Data : {}

  switch (errorCode) {
    case apiErrorCodes.PHONE_NUMBER_NOT_VALID:
      return { error: '', phoneIsValid: false, duplicateLicensesInfo: null }
    case apiErrorCodes.THIRD_PARTY_ERROR:
      return {
        error: `${getString(data.ErrorCode)} - ${getString(data.ErrorMessage)}`,
        phoneIsValid: false,
        duplicateLicensesInfo: null,
      }
    case apiErrorCodes.PHONE_NUMBER_NOT_UNIQUE:
      return {
        error: getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'EmailNotValid':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Email'),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'LicenseNotUnique':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS, null),
        phoneIsValid: null,
        duplicateLicensesInfo: {
          licenseNumber: getString(data.Number),
          stateCode: getString(data.StateCode),
          licensedBy: getString(data.LicensedBy),
        },
      }
    case 'FirstNameNotValid':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'First name'),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case apiErrorCodes.LICENSE_VISIT_TYPE_COUNT_NOT_VALID:
      return {
        error: getUserFriendlyErrorMessage(errorCodes.LICENSE_WITHOUT_ONE_SERVICE_TYPE, null),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case apiErrorCodes.VISIT_TYPE_NOT_FOUND:
      return {
        error: getUserFriendlyErrorMessage(errorCodes.ITEM_DOES_NOT_EXIST, null, VISIT_TYPE),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'LastNameNotValid':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Last name'),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'EmailNotUnique':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email'),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'EmailCannotBeModified':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.ALREADY_ACTIVATED_USER, null),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    case 'InternalServerError':
      return {
        error: getUserFriendlyErrorMessage(errorCodes.DEFAULT),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
    default:
      return {
        error: getUserFriendlyErrorMessage(errorCodes.DEFAULT),
        phoneIsValid: null,
        duplicateLicensesInfo: null,
      }
  }
}
